import React, { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import { Download, Share2, X } from "lucide-react";

function isStandalone() {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  } catch (_e) { return false; }
}

function isIOS() {
  try {
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  } catch (_e) { return false; }
}

/**
 * InstallAppPrompt — Bottom-anchored banner that:
 *   • Captures the `beforeinstallprompt` event on Chrome/Edge/Android and shows
 *     a one-tap install CTA.
 *   • On iOS Safari shows a hint with the share-icon flow (browsers don't allow
 *     programmatic install on iOS).
 *
 * Hidden when the app is already installed. Dismissals are NOT persisted across
 * page loads so users always see the banner until they actually install (the
 * permanent header InstallButton stays available regardless).
 */
export default function InstallAppPrompt() {
  const { t } = useT();
  const deferred = useRef(null);
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState(null); // 'android' | 'ios'

  useEffect(() => {
    if (isStandalone()) return undefined;

    const onBefore = (e) => {
      e.preventDefault();
      deferred.current = e;
      setVariant("android");
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
    };

    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback — show after 2 s if no beforeinstallprompt fires.
    let iosTimer = null;
    if (isIOS()) {
      iosTimer = setTimeout(() => {
        if (!isStandalone()) {
          setVariant("ios");
          setShow(true);
        }
      }, 2000);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  // Local-only dismiss: hides the banner for this page-load only. The user can
  // re-trigger install from the permanent header button at any time.
  const dismiss = () => setShow(false);

  const onInstall = async () => {
    const ev = deferred.current;
    if (!ev) return;
    try {
      ev.prompt();
      await ev.userChoice;
    } catch (_e) { /* ignore */ }
    deferred.current = null;
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-24 sm:bottom-28 z-40 flex justify-center px-4 pointer-events-none"
      data-testid="install-prompt"
    >
      <div className="pointer-events-auto w-full max-w-md rounded-2xl bg-zinc-950 text-white shadow-2xl shadow-black/40 border border-white/10 px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-fresh-green text-black flex items-center justify-center shrink-0">
          <Download className="w-5 h-5" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-semibold">{t("install.title")}</div>
          {variant === "ios" ? (
            <div className="text-[11px] text-white/70 mt-0.5 leading-snug flex items-center gap-1 flex-wrap">
              {t("install.ios.share")}
              <Share2 className="w-3.5 h-3.5" strokeWidth={2.4} />
              {t("install.ios.then")}
            </div>
          ) : (
            <div className="text-[11px] text-white/70 mt-0.5 leading-snug">{t("install.desc.android")}</div>
          )}
        </div>
        {variant === "android" ? (
          <button
            onClick={onInstall}
            className="h-9 px-3 rounded-full bg-fresh-green text-black text-xs font-bold active:scale-95"
            data-testid="install-prompt-cta"
          >
            {t("install.btn")}
          </button>
        ) : (
          <button
            onClick={dismiss}
            className="h-9 px-3 rounded-full bg-white/10 text-white text-xs font-semibold active:scale-95"
            data-testid="install-prompt-dismiss"
          >
            OK
          </button>
        )}
        <button
          onClick={dismiss}
          className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center shrink-0"
          aria-label="dismiss"
          data-testid="install-prompt-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
